// src/components/MarkdownContainer.tsx

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import Prism from 'prismjs';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-javascript';
import './MarkdownContainer.scss';

interface MarkdownContainerProps {
    content: string | undefined;
    type?: 'markdown' | 'json' | 'function' | 'xml';
    expandedDefault?: boolean;
}

export const MarkdownContainer: React.FC<MarkdownContainerProps> = ({
    content,
    type = 'markdown',
    expandedDefault = false
}) => {
    const [isExpanded, setIsExpanded] = useState(expandedDefault);
    const [isExpandable, setIsExpandable] = useState(false);
    const [contentHeight, setContentHeight] = useState<number>(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const LINE_HEIGHT = 16;
    const MAX_LINES = 5;

    const formatContent = (content: string | undefined): { formatted: string, language: string } => {
        if (!content) return { formatted: '', language: 'none' };

        try {
            switch (type) {
                case 'json': {
                    const parsed = JSON.parse(content);
                    return {
                        formatted: JSON.stringify(parsed, null, 2),
                        language: 'json'
                    };
                }
                case 'xml': {
                    return {
                        formatted: content,
                        language: 'xml'
                    };
                }
                case 'function': {
                    const { name, arguments: args } = JSON.parse(content);
                    return {
                        formatted: `${name}(${args})`,
                        language: 'javascript'
                    };
                }
                default:
                    return {
                        formatted: content,
                        language: 'none'
                    };
            }
        } catch (e) {
            return {
                formatted: content,
                language: 'none'
            };
        }
    };

    const updateHeight = () => {
        if (contentRef.current) {
            // Force a reflow to ensure accurate measurements
            void contentRef.current.offsetHeight;

            const scrollHeight = contentRef.current.scrollHeight;
            const isContentExpandable = scrollHeight > (LINE_HEIGHT * MAX_LINES);

            setContentHeight(scrollHeight);
            setIsExpandable(isContentExpandable);

            if (!isContentExpandable) {
                setIsExpanded(false);
            }
        }
    };

    useEffect(() => {
        const highlight = () => {
            if (contentRef.current) {
                Prism.highlightAllUnder(contentRef.current);
            }
        };

        // Setup ResizeObserver
        resizeObserverRef.current = new ResizeObserver((entries) => {
            updateHeight();
        });

        if (contentRef.current) {
            resizeObserverRef.current.observe(contentRef.current);
        }

        // Initial highlight and height calculation
        highlight();

        // Use requestAnimationFrame to ensure content is rendered
        requestAnimationFrame(() => {
            updateHeight();
        });

        // Handle window resize
        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, [content]);

    const handleClick = () => {
        if (isExpandable) {
            setIsExpanded(!isExpanded);
        }
    };

    const { formatted, language } = formatContent(content);

    return (
        <div
            data-component="MarkdownContainer"
            className={isExpandable ? 'expandable' : ''}
            style={{
                '--content-height': `${contentHeight}px`,
                '--collapsed-height': `${LINE_HEIGHT * MAX_LINES}px`
            } as React.CSSProperties}
        >
            <div
                ref={contentRef}
                className={`markdown-content ${!isExpanded && isExpandable ? 'collapsed' : ''}`}
                onClick={handleClick}
            >
                {language !== 'none' ? (
                    <code className={`language-${language}`}>
                        {formatted}
                    </code>
                ) : (
                    <div className="text-content">
                        {formatted}
                    </div>
                )}
            </div>
            <div className={!isExpanded && isExpandable ? 'fade visible' : 'fade'} />
            <button
                className={isExpandable ? 'expand-button visible' : 'expand-button'}
                onClick={handleClick}
            >
                <span className="button-text">
                    {isExpanded ? (
                        <>Show Less <ChevronUp size={14} /></>
                    ) : (
                        <>Show More <ChevronDown size={14} /></>
                    )}
                </span>
            </button>
        </div>
    );
};